import React, { useEffect, useState } from "react";
import axios from "axios";
import "./Product.css";
import { motion } from "framer-motion";
import { toast } from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { addToCart, addToCartAsync } from "../../../Features/Cart/cartSlice";
import RelatedProducts from "../RelatedProducts/RelatedProducts";

const Product = () => {
  const { id: UserId } = useParams();
  const USER = useSelector((state) => state.user.user?._id);
  const dispatch = useDispatch();
  const cartItems = useSelector((state) => state.cart.items);
  const [quantity, setQuantity] = useState(1);
  const [showFull, setShowFull] = useState(false);
  const [Medicine, setMedicine] = useState(null);
  const [relatedMedicines, setRelatedMedicines] = useState([]);
  const [activeTab, setActiveTab] = useState("details"); // New state for tabs

  useEffect(() => {
    const fetchMedicine = async (UserId) => {
      try {
        const response = await axios.get(
          `https://api.swasthmitra.com/api/medicine/${UserId}`
        );
        setMedicine(response.data);
      } catch (error) {
        console.error("Error fetching medicine:", error);
      }
    };
    
    const fetchRelatedMedicines = async (UserId) => {
      try {
        const response = await axios.get(
          `https://api.swasthmitra.com/api/medicine/${UserId}/related`
        );
        setRelatedMedicines(response.data);
      } catch (error) {
        console.error("Error fetching related medicines:", error);
      }
    };
    
    // Reset state when product ID changes
    setMedicine(null);
    setRelatedMedicines([]);
    
    if (UserId) {
      fetchMedicine(UserId);
      fetchRelatedMedicines(UserId);
    }
  }, [UserId]);

  // Helper function to calculate discount percentage
  const calculateDiscount = (mrp, dealRate) => {
    if (!mrp || !dealRate || mrp <= dealRate) return 0;
    return Math.round(((mrp - dealRate) / mrp) * 100);
  };

  const medicine = {
    id: Medicine?._id,
    name: Medicine?.name,
    brand: Medicine?.brand,
    category: Medicine?.category,
    price: Medicine?.dealRate,
    mrp: Medicine?.mrp,
    discount: calculateDiscount(Medicine?.mrp, Medicine?.dealRate),
    stock: Medicine?.stockQuantity,
    description: Medicine?.description,
    // dosage: "1-2 tablets every 4-6 hours as needed",
    // composition: "Each tablet contains Paracetamol BP 500mg",
    images: Medicine?.imageUrl,
    imageUrls: Medicine?.imageUrls,
    features: [
      "Fast pain relief",
      "Fever reduction",
      "Gentle on stomach",
      "No drowsiness",
    ],
    rating: 4.8,
    reviews: 826,
  };

  const [selectedImage, setSelectedImage] = useState();

  const words = medicine?.description?.split(" ");
  const shortDescription =
    words?.slice(0, 60).join(" ") + (words?.length > 60 ? "..." : "");
  const handleQuantityChange = (value) => {
    const newQuantity = Math.max(1, Math.min(20, quantity + value));
    setQuantity(newQuantity);
  };

  const handleAddToCart = async () => {
    if (!USER) {
      toast.error("Please login to add items to cart");
      return;
    }
    const cartItem = {
      productID: Medicine._id,
      productName: Medicine.name,
      dealRate: parseFloat(Medicine.dealRate),
      productImage: medicine.images || "",
      quantity: 1,
      batchNumber: Medicine.batchNumber,
      brand: Medicine.brand,
      packOf: Medicine.packOf,
      gst: Medicine.gst,
      mrp: Medicine.mrp,
      expiryDate: Medicine.expiryDate,
    };

    try {
      await dispatch(
        addToCartAsync({
          userId: USER,
          productID: Medicine._id,
          quantity: 1,
        })
      );
      dispatch(addToCart(cartItem));
      toast.success(`${medicine.name} added to cart!`);
    } catch (error) {
      console.error("Error adding to cart:", error);
      toast.error("Failed to add item to cart");
    }
  };

  if (!UserId) {
    return <div>Invalid product ID</div>;
  }

  // Show loading state while fetching data
  if (!Medicine) {
    return <div>Loading product details...</div>;
  }

  return (
    <div className="product-details-container">
      <motion.div
        className="product-details-wrapper"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="product-images-section">
          {/* Main Product Image */}
          <div className="product-main-image">
            <img
              src={selectedImage || medicine?.images || Medicine?.imageUrls[0]}
              alt="Product"
            />
          </div>

          {/* Thumbnail Images */}
          <div className="thumbnail-images">
            {Medicine?.imageUrls?.map((img, index) => (
              <img
                key={index}
                src={img}
                alt={`Thumbnail ${index + 1}`}
                onClick={() => setSelectedImage(img)}
                className={selectedImage === img ? "selected" : ""}
              />
            ))}
          </div>
        </div>

        <div className="product-info-section">
          <div className="product-header">
            <h1>{medicine.name}</h1>
            <p className="brand">By {medicine.brand}</p>
          </div>

          <div className="product-pricing">
            <div className="price-tag">
              <span className="current-price">
                ₹{Math.round(medicine.price)}
              </span>
              {medicine.mrp > medicine.price && (
                <>
                  <span className="original-price">₹{medicine.mrp}</span>
                  <span className="discount">-{medicine.discount}%</span>
                </>
              )}
            </div>
            <p className="tax-info">Inclusive of all taxes</p>
          </div>

          {medicine.stock > 0 ? (
            <div
              className={`add-to-carting ${
                cartItems.some((item) => item.productID === medicine.id)
                  ? "disabled"
                  : ""
              }`}
              onClick={() => {
                if (!cartItems.some((item) => item.productID === medicine.id)) {
                  handleAddToCart();
                }
              }}
            >
              {cartItems.some((item) => item.productID === medicine.id)
                ? "In Cart"
                : "Add to Cart"}
            </div>
          ) : (
            <button className="out-of-stock" disabled>
              Out of Stock
            </button>
          )}
        </div>
      </motion.div>
      
      {/* Move Related Products Section Here - Before Description */}
      {relatedMedicines.length > 0 && (
        <div className="related-products-section">
          <h2>Same Cure, <span style={{ color: "#f04608" }}> Big Discounts!!!</span></h2>
          <RelatedProducts medicines={relatedMedicines.slice(0, 6)} />
        </div>
      )}
      
      {/* Product Details and Description Tabs */}
      <div className="product-tabs">
        <div className="tabs-header">
          <button
            className={`tab-button ${activeTab === "details" ? "active" : ""}`}
            onClick={() => setActiveTab("details")}
          >
            Product Details
          </button>
          <button
            className={`tab-button ${activeTab === "description" ? "active" : ""}`}
            onClick={() => setActiveTab("description")}
          >
            Description
          </button>
        </div>
        
        <div className="tab-content">
          {activeTab === "details" && (
            <div className="product-details-content">
              <div className="product-detail-item">
                <span className="detail-label">Category:</span>
                <span className="detail-value">{medicine.category}</span>
              </div>
              <div className="product-detail-item">
                <span className="detail-label">Brand:</span>
                <span className="detail-value">{medicine.brand}</span>
              </div>

              {/* <div className="product-detail-item">
                <span className="detail-label">Composition:</span>
                <span className="detail-value">{medicine.composition}</span>
              </div>
              <div className="product-detail-item">
                <span className="detail-label">Dosage:</span>
                <span className="detail-value">{medicine.dosage}</span>
              </div> */}

              <div className="product-detail-item">
                <span className="detail-label">MRP:</span>
                <span className="detail-value">₹{medicine.mrp}</span>
              </div>
              <div className="product-detail-item">
                <span className="detail-label">Deal Rate:</span>
                <span className="detail-value">₹{Math.round(medicine.price)}</span>
              </div>
              <div className="product-detail-item">
                <span className="detail-label">Discount:</span>
                <span className="detail-value">{medicine.discount}%</span>
              </div>
            </div>
          )}
          
          {activeTab === "description" && (
            <div className="product-description-content">
              <p dangerouslySetInnerHTML={{ __html: medicine.description }} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Product;